import * as Sentry from '@sentry/react';

const isDev = import.meta.env.DEV;

/**
 * Centralized logging utility that sends logs to Sentry.
 * In development, logs are also printed to the browser console.
 *
 * @example
 * import logger from 'src/utils/logger';
 *
 * // For regular informational logs
 * logger.info('User logged in', { userId: 123 });
 *
 * // For error EVENTS (not exceptions) - like business logic errors
 * logger.error('Payment validation failed', { orderId: 'order_123', reason: 'insufficient_funds' });
 *
 * // For EXCEPTIONS - use captureException (gives you stack traces!)
 * try { ... } catch (e) {
 *   logger.captureException(e);
 * }
 *
 * // For structured logs with variables
 * logger.debug(logger.fmt`Cache miss for user: ${userId}`);
 */
export const logger = {
  /**
   * Capture an exception and send it to Sentry with full stack trace.
   *
   * **USE THIS for:**
   * - Caught exceptions in try-catch blocks
   * - Error objects
   * - Any situation where you want stack traces and proper error grouping
   *
   * **DON'T use logger.error() for exceptions** - it won't give you stack traces!
   *
   * @example
   * try {
   *   await riskyOperation();
   * } catch (e) {
   *   logger.captureException(e, {
   *     tags: { component: 'PaymentProcessor' },
   *     extra: { orderId: '123' }
   *   });
   * }
   */
  captureException: Sentry.captureException,

  /**
   * Trace level - most verbose, for detailed debugging
   */
  trace: (message: string, data?: Record<string, unknown>) => {
    if (isDev) console.debug('[TRACE]', message, data ?? '');
    Sentry.logger.trace(message, data);
  },

  /**
   * Debug level - for debugging information
   */
  debug: (message: string, data?: Record<string, unknown>) => {
    if (isDev) console.debug('[DEBUG]', message, data ?? '');
    Sentry.logger.debug(message, data);
  },

  /**
   * Info level - for general information
   */
  info: (message: string, data?: Record<string, unknown>) => {
    if (isDev) console.info('[INFO]', message, data ?? '');
    Sentry.logger.info(message, data);
  },

  /**
   * Warn level - for warnings that don't stop execution
   */
  warn: (message: string, data?: Record<string, unknown>) => {
    if (isDev) console.warn('[WARN]', message, data ?? '');
    Sentry.logger.warn(message, data);
  },

  /**
   * Error level - for error EVENTS (not exceptions).
   *
   * **USE THIS for:**
   * - Business logic errors (validation failures, state errors, etc.)
   * - Error messages that don't have an exception object
   *
   * **DON'T use this for exceptions** - use captureException() instead!
   *
   * @example
   * logger.error('Payment validation failed', { orderId: '123', reason: 'insufficient_funds' });
   */
  error: (message: string, data?: Record<string, unknown>) => {
    if (isDev) console.error('[ERROR]', message, data ?? '');
    Sentry.logger.error(message, data);
  },

  /**
   * Fatal level - for critical errors
   */
  fatal: (message: string, data?: Record<string, unknown>) => {
    if (isDev) console.error('[FATAL]', message, data ?? '');
    Sentry.logger.fatal(message, data);
  },

  /**
   * Template literal function for structured logs with variables
   * @example logger.debug(logger.fmt`Cache miss for user: ${userId}`);
   */
  fmt: Sentry.logger.fmt,
};

export default logger;
